import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { addMonths, endOfMonth, endOfWeek, format, isSameMonth, isToday, startOfMonth, startOfWeek, eachDayOfInterval } from "date-fns";
import { ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PageHeader } from "@/components/Brand";
import AppointmentDialog from "@/components/AppointmentDialog";
import { api, useData } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TEMP_DOT, TEMP_STYLE, TEMPERATURES } from "@/lib/crm";

const MONTH_KEY = "avy_calendar_month";

export default function CalendarPage() {
  const location = useLocation();
  const { user } = useAuth();
  const monthKey = `${MONTH_KEY}:${user?.id || "anon"}`;
  const [contacts] = useData("/contacts", []);
  const [appts, reload] = useData("/appointments", []);
  const [month, setMonth] = useState(() => {
    const saved = localStorage.getItem(`${MONTH_KEY}:${user?.id || "anon"}`);
    const d = saved ? new Date(saved) : new Date();
    return isNaN(d) ? new Date() : d;
  });
  const [dlg, setDlg] = useState({ open: false, initial: {} });
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    localStorage.setItem(monthKey, month.toISOString());
  }, [monthKey, month]);

  useEffect(() => {
    const cid = location.state?.contactId;
    if (cid) setDlg({ open: true, initial: { contact_id: cid, date: format(new Date(), "yyyy-MM-dd") } });
  }, [location.state]);

  const days = eachDayOfInterval({ start: startOfWeek(startOfMonth(month)), end: endOfWeek(endOfMonth(month)) });
  const upcoming = appts.filter((a) => a.date >= format(new Date(), "yyyy-MM-dd")).slice(0, 8);
  const tempOf = (a) => contacts.find((c) => c.id === a.contact_id)?.temperature;

  const cancel = async () => {
    if (!toDelete) return;
    await api.delete(`/appointments/${toDelete.id}`);
    toast.success("Appointment cancelled");
    setToDelete(null);
    reload();
  };

  return (
    <div>
      <PageHeader title="Calendar" subtitle="Appointments linked to your contacts. Click any day to schedule on that date.">
        <Button onClick={() => setDlg({ open: true, initial: { date: format(new Date(), "yyyy-MM-dd") } })} className="bg-[#3B185F]" data-testid="new-appointment-btn"><Plus className="h-4 w-4 mr-1.5" />New Appointment</Button>
      </PageHeader>

      <div className="grid gap-6 xl:grid-cols-[1fr_320px]">
        <div className="rounded-2xl border bg-white p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl font-bold text-[#19082B]" data-testid="calendar-month-label">{format(month, "MMMM yyyy")}</h2>
            <div className="flex gap-1">
              <Button size="icon" variant="outline" onClick={() => setMonth(addMonths(month, -1))} data-testid="calendar-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" onClick={() => setMonth(new Date())} data-testid="calendar-today-btn">Today</Button>
              <Button size="icon" variant="outline" onClick={() => setMonth(addMonths(month, 1))} data-testid="calendar-next-btn"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="flex items-center gap-4 mb-3" data-testid="calendar-temp-legend">
            <span className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">Lead temperature</span>
            {TEMPERATURES.map((t) => (
              <span key={t} className="flex items-center gap-1.5 text-xs text-stone-600" data-testid={`calendar-legend-${t}`}>
                <span className={`h-2.5 w-2.5 rounded-full ${TEMP_DOT[t]}`} />{t}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-7 text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="p-2">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((d) => {
              const key = format(d, "yyyy-MM-dd");
              const list = appts.filter((a) => a.date === key);
              return (
                <button key={key} type="button" onClick={() => setDlg({ open: true, initial: { date: key } })}
                  className={`min-h-[92px] rounded-lg border p-1.5 text-left align-top transition-colors hover:border-[#D4AF37] hover:bg-[#FFFDF0] ${isSameMonth(d, month) ? "bg-white" : "bg-stone-50 text-stone-400"}`}
                  data-testid={`calendar-day-${key}`}>
                  <div className={`text-xs font-semibold mb-1 ${isToday(d) ? "inline-grid h-6 w-6 place-items-center rounded-full bg-[#3B185F] text-white" : ""}`}>{format(d, "d")}</div>
                  {list.map((a) => {
                    const temp = tempOf(a);
                    return (
                      <div key={a.id} className="mb-1 flex items-center gap-1 truncate rounded bg-[#3B185F] px-1.5 py-0.5 text-[10px] text-white" title={`${a.title} · ${a.contact_name}${temp ? ` · ${temp}` : ""}`} data-testid={`calendar-appt-${a.id}`}>
                        {temp && <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${TEMP_DOT[temp]}`} data-testid={`calendar-appt-temp-${a.id}`} />}
                        <span className="text-[#F3C623]">{a.time}</span> <span className="truncate">{a.title}</span>
                      </div>
                    );
                  })}
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl bg-[#19082B] p-5 text-white h-fit">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-bold mb-4">Upcoming</div>
          {!upcoming.length && <p className="text-sm text-white/60">No upcoming appointments.</p>}
          <div className="space-y-3" data-testid="upcoming-appointments">
            {upcoming.map((a) => {
              const temp = tempOf(a);
              return (
              <div key={a.id} className="rounded-xl bg-white/5 border border-white/10 p-3" data-testid={`upcoming-appt-${a.id}`}>
                <div className="flex justify-between gap-2">
                  <div className="font-semibold text-sm flex items-center gap-1.5">{a.title}{temp && <span className={`rounded-full border px-1.5 text-[9px] font-bold ${TEMP_STYLE[temp]}`} data-testid={`upcoming-appt-temp-${a.id}`}>{temp}</span>}</div>
                  <button onClick={() => setToDelete(a)} className="text-white/40 hover:text-red-300" data-testid={`cancel-appt-${a.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
                <div className="text-xs text-[#F3C623]">{a.date} · {a.time}</div>
                <div className="text-xs text-white/70">with {a.contact_name}</div>
                {a.purpose && <div className="text-[10px] uppercase tracking-wider text-[#D4AF37] mt-0.5">{a.purpose}</div>}
                {a.description && <div className="text-xs text-white/50 mt-1 line-clamp-2">{a.description}</div>}
              </div>
              );
            })}
          </div>
        </div>
      </div>

      <AppointmentDialog open={dlg.open} initial={dlg.initial} contacts={contacts} onOpenChange={(v) => setDlg((s) => ({ ...s, open: v }))} onSaved={reload} />

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent data-testid="cancel-appt-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this appointment?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete ? `"${toDelete.title}" with ${toDelete.contact_name} on ${toDelete.date} at ${toDelete.time} will be permanently removed.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-appt-dismiss">Keep appointment</AlertDialogCancel>
            <AlertDialogAction onClick={cancel} className="bg-red-600 hover:bg-red-700" data-testid="cancel-appt-confirm">Cancel appointment</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
